import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEllipsisVertical, faFaceSmile, faCheck, faXmark, faShare
} from '@fortawesome/free-solid-svg-icons';

import '../../styles/messageActions.css';

/**
 * The WhatsApp-style controls every message gets: the three-dot menu, the quick
 * reaction bar, the reaction chips under the bubble, and select-mode pieces.
 *
 * Shared by the chat module and the task discussion. The two threads look
 * different, but a reaction or a copy should behave the same in both. Each
 * surface decides which actions it offers by what it passes in; nothing here
 * knows about either API.
 */

/**
 * The four quick reactions, in WhatsApp's order. Must match REACTIONS in
 * server/utils/reactions.js. Escapes for the same reason as there: the heart
 * has an invisible variation selector that has to survive the round trip.
 */
export const QUICK_REACTIONS = [
    '\u{1F44D}',
    '❤️',
    '\u{1F602}',
    '\u{1F62E}'
];

const idOf = (u) => String(u?._id || u || '');

/** The emoji this person has put on a message, or null. */
export const myReaction = (reactions, myId) =>
    (reactions || []).find((r) => idOf(r.user) === String(myId))?.emoji || null;

/**
 * What the server will do with a reaction, done locally first so the chip
 * appears on the click rather than a round trip later. Mirrors applyReaction().
 */
export const applyReactionLocal = (reactions = [], me, emoji) => {
    const existing = reactions.find((r) => idOf(r.user) === String(me._id));
    const rest = reactions.filter((r) => idOf(r.user) !== String(me._id));
    if (existing && existing.emoji === emoji) return rest;
    return [...rest, { user: { _id: me._id, name: me.name }, emoji, at: new Date().toISOString() }];
};

/** Reactions grouped by emoji, quick-reaction order first. */
export const summariseReactions = (reactions = [], myId) => {
    const groups = new Map();
    reactions.forEach((r) => {
        const mine = idOf(r.user) === String(myId);
        const g = groups.get(r.emoji) || { emoji: r.emoji, count: 0, names: [], mine: false };
        g.count += 1;
        g.names.push(mine ? 'You' : (r.user?.name || 'Someone'));
        g.mine = g.mine || mine;
        groups.set(r.emoji, g);
    });
    return [...groups.values()].sort((a, b) => {
        const ia = QUICK_REACTIONS.indexOf(a.emoji);
        const ib = QUICK_REACTIONS.indexOf(b.emoji);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
};

/**
 * Put text on the clipboard.
 *
 * The Clipboard API only exists on secure origins, and the portal is reached
 * over plain http on the office network too. There the old execCommand route
 * is the only one that works.
 */
export const copyText = async (text, label = 'Copied') => {
    try {
        await navigator.clipboard.writeText(text);
    } catch {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } finally { ta.remove(); }
    }
    Swal.fire({
        toast: true, position: 'bottom', icon: 'success', title: label,
        timer: 1400, showConfirmButton: false
    });
};

/**
 * Several messages as one block of text, in the format WhatsApp copies them:
 * "[15/09/2026, 14:05] Riya: text". Oldest first, whatever order they were
 * ticked in.
 */
export const transcript = (rows) => rows
    .slice()
    .sort((a, b) => new Date(a.at) - new Date(b.at))
    .map(({ at, name, text }) => {
        const d = new Date(at);
        const date = d.toLocaleDateString('en-GB');
        const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        return `[${date}, ${time}] ${name}: ${text}`;
    })
    .join('\n');

/**
 * A popover pinned to a button, drawn on document.body.
 *
 * Rendered outside the thread so its overflow cannot clip it, and flipped to
 * the other side of the button near a screen edge. A menu whose last item is
 * below the fold is a menu with a missing option.
 */
const AnchoredPopover = ({ anchor, onClose, align = 'right', placement = 'bottom', className = '', children }) => {
    const ref = useRef(null);
    const [pos, setPos] = useState({ top: -9999, left: -9999 });

    useLayoutEffect(() => {
        const el = ref.current;
        if (!anchor || !el) return;
        const a = anchor.getBoundingClientRect();
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        const gap = 4;
        const margin = 8;

        const below = a.bottom + gap;
        const above = a.top - h - gap;
        let top = placement === 'top' ? above : below;
        if (placement === 'top' && top < margin) top = below;
        if (placement !== 'top' && top + h > window.innerHeight - margin) top = above;
        top = Math.max(margin, Math.min(top, window.innerHeight - h - margin));

        let left = align === 'right' ? a.right - w : a.left;
        left = Math.max(margin, Math.min(left, window.innerWidth - w - margin));

        setPos({ top, left });
    }, [anchor, align, placement]);

    useEffect(() => {
        const away = (e) => {
            if (ref.current?.contains(e.target) || anchor?.contains(e.target)) return;
            onClose();
        };
        const key = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('mousedown', away);
        document.addEventListener('keydown', key);
        window.addEventListener('scroll', onClose, true);
        window.addEventListener('resize', onClose);
        return () => {
            document.removeEventListener('mousedown', away);
            document.removeEventListener('keydown', key);
            window.removeEventListener('scroll', onClose, true);
            window.removeEventListener('resize', onClose);
        };
    }, [anchor, onClose]);

    return createPortal(
        <div ref={ref} className={`mx-pop ${className}`} style={{ top: pos.top, left: pos.left }}>
            {children}
        </div>,
        document.body
    );
};

/**
 * The three dots on a message. `items` is a list of
 * { key, label, icon, onClick, danger }, and falsy entries are skipped, so a
 * caller can write `canEdit && {...}` inline.
 */
export const MessageMenu = ({ items, align = 'right', className = '' }) => {
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const close = React.useCallback(() => setOpen(false), []);

    const visible = (items || []).filter(Boolean);
    if (!visible.length) return null;

    return (
        <>
            <button
                type="button"
                ref={btnRef}
                className={`mx-dots ${open ? 'is-open' : ''} ${className}`}
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                title="More options"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <FontAwesomeIcon icon={faEllipsisVertical} />
            </button>

            {open && (
                <AnchoredPopover anchor={btnRef.current} align={align} onClose={close} className="mx-menu">
                    <div role="menu">
                        {visible.map((it) => (
                            <button
                                key={it.key}
                                type="button"
                                role="menuitem"
                                className={`mx-menu-item ${it.danger ? 'danger' : ''}`}
                                onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick(); }}
                            >
                                <FontAwesomeIcon icon={it.icon} fixedWidth />
                                {it.label}
                            </button>
                        ))}
                    </div>
                </AnchoredPopover>
            )}
        </>
    );
};

/** The smiley beside a bubble, which opens the four quick reactions. */
export const ReactionPicker = ({ myEmoji, onPick, align = 'left', className = '' }) => {
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const close = React.useCallback(() => setOpen(false), []);

    return (
        <>
            <button
                type="button"
                ref={btnRef}
                className={`mx-react-btn ${open ? 'is-open' : ''} ${className}`}
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                title="React"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <FontAwesomeIcon icon={faFaceSmile} />
            </button>

            {open && (
                <AnchoredPopover
                    anchor={btnRef.current}
                    align={align}
                    placement="top"
                    onClose={close}
                    className="mx-react-bar"
                >
                    {QUICK_REACTIONS.map((emoji) => (
                        <button
                            key={emoji}
                            type="button"
                            className={myEmoji === emoji ? 'is-mine' : ''}
                            onClick={(e) => { e.stopPropagation(); setOpen(false); onPick(emoji); }}
                            title={myEmoji === emoji ? 'Remove reaction' : 'React'}
                        >
                            {emoji}
                        </button>
                    ))}
                </AnchoredPopover>
            )}
        </>
    );
};

/**
 * The reactions under a bubble. Hovering a chip names who reacted. Clicking
 * one reacts with that emoji, or takes yours back if it was already yours.
 */
export const ReactionChips = ({ reactions, myId, onToggle, className = '' }) => {
    const groups = summariseReactions(reactions, myId);
    if (!groups.length) return null;

    return (
        <div className={`mx-chips ${className}`}>
            {groups.map((g) => (
                <button
                    key={g.emoji}
                    type="button"
                    className={`mx-chip ${g.mine ? 'is-mine' : ''}`}
                    title={g.names.join(', ')}
                    disabled={!onToggle}
                    onClick={(e) => { e.stopPropagation(); onToggle?.(g.emoji); }}
                >
                    <span className="mx-chip-emoji">{g.emoji}</span>
                    {g.count > 1 && <span className="mx-chip-count">{g.count}</span>}
                </button>
            ))}
        </div>
    );
};

/** The round tick shown beside each message while selecting. */
export const SelectCheck = ({ checked }) => (
    <span className={`mx-check ${checked ? 'is-checked' : ''}`} aria-hidden="true">
        {checked && <FontAwesomeIcon icon={faCheck} />}
    </span>
);

/** "Forwarded", above the content of a forwarded message. */
export const ForwardedLabel = () => (
    <div className="mx-forwarded">
        <FontAwesomeIcon icon={faShare} />
        Forwarded
    </div>
);

/**
 * The bar that replaces a thread's header while messages are selected.
 * `actions` are { key, label, icon, onClick, danger, disabled }, and falsy
 * entries are skipped.
 */
export const SelectionBar = ({ count, onCancel, actions = [], className = '' }) => (
    <div className={`mx-selectbar ${className}`}>
        <button type="button" className="mx-selectbar-close" onClick={onCancel} title="Cancel selection">
            <FontAwesomeIcon icon={faXmark} />
        </button>
        <strong>{count} selected</strong>
        <div className="mx-selectbar-actions">
            {actions.filter(Boolean).map((a) => (
                <button
                    key={a.key}
                    type="button"
                    className={a.danger ? 'danger' : ''}
                    onClick={a.onClick}
                    disabled={!count || a.disabled}
                    title={a.title || a.label}
                >
                    <FontAwesomeIcon icon={a.icon} />
                    <span>{a.label}</span>
                </button>
            ))}
        </div>
    </div>
);
